import {StepMeta, StepMetaRun} from '../types';

/**
 * Strategy for resolving collisions when multiple steps map to the same slider value
 */
export type SliderStepCollisionStrategy = 'latest' | 'earliest' | 'custom';

/**
 * Mapping between a step value and its corresponding slider step value
 */
export interface SliderStepMapping {
  step: number;
  sliderStep: number;
}

/**
 * Result of resolving slider step collisions
 */
export interface SliderStepCollisionResult {
  uniqueSliderSteps: number[];
  stepsBySliderStep: Map<number, number>;
  resolvedSteps: number[];
}

/**
 * Resolves collisions when multiple steps map to the same slider value.
 * @param mappings Array of step to slider step mappings
 * @param strategy Strategy to use for resolving collisions
 * @param customResolver Optional custom resolver function
 * @returns Object containing the unique slider steps and their corresponding step values
 * @throws Error if using 'custom' strategy without providing a customResolver
 */
export function resolveSliderStepCollisions(
  mappings: SliderStepMapping[],
  strategy: SliderStepCollisionStrategy = 'latest',
  customResolver?: (steps: number[]) => number
): SliderStepCollisionResult {
  // Group steps by slider step
  const stepsBySliderStep = new Map<number, number[]>();
  mappings.forEach(({step, sliderStep}) => {
    if (!stepsBySliderStep.has(sliderStep)) {
      stepsBySliderStep.set(sliderStep, []);
    }
    stepsBySliderStep.get(sliderStep)!.push(step);
  });

  // Resolve collisions based on strategy
  const resolvedStepsBySliderStep = new Map<number, number>();
  const uniqueSliderSteps: number[] = [];
  const resolvedSteps: number[] = [];

  // Process slider steps in order
  Array.from(stepsBySliderStep.keys()).forEach(sliderStep => {
    const steps = stepsBySliderStep.get(sliderStep)!;
    let resolvedStep: number;

    switch (strategy) {
      case 'latest':
        resolvedStep = Math.max(...steps);
        break;
      case 'earliest':
        resolvedStep = Math.min(...steps);
        break;
      case 'custom':
        if (!customResolver) {
          throw new Error('Custom resolver required for custom strategy');
        }
        resolvedStep = customResolver(steps);
        break;
      default:
        resolvedStep = Math.max(...steps); // Default to latest
    }

    uniqueSliderSteps.push(sliderStep);
    resolvedSteps.push(resolvedStep);
    resolvedStepsBySliderStep.set(sliderStep, resolvedStep);
  });

  return {
    uniqueSliderSteps,
    stepsBySliderStep: resolvedStepsBySliderStep,
    resolvedSteps,
  };
}

export function getClosestLowerStep(
  min: number,
  max: number,
  steps: number[],
  target: number
): number {
  if (target <= min) {
    return min;
  }
  if (target >= max) {
    return max;
  }

  // Handle empty steps array
  if (steps.length === 0) {
    return max;
  }

  return steps.reduce((prev, curr) =>
    Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
  );
}

/**
 * Advanced tick snapping function that considers direction and uses binary search.
 * This is used for slider interactions where we want to snap to the nearest valid step
 * in the direction the user is moving.
 *
 * @param val Current value to snap
 * @param prev Previous value (used to determine direction)
 * @param min Minimum allowed value
 * @param max Maximum allowed value
 * @param ticks Array of valid tick values (should be sorted)
 * @returns The closest valid tick value in the appropriate direction
 */
export function getClosestTickWithDirection(
  val: number,
  prev: number,
  min: number,
  max: number,
  ticks?: number[]
): number {
  // if min/max not in ticks, allow coercion to nearest value
  if (val > max) {
    return max;
  }
  if (val < min) {
    return min;
  }
  if (ticks === null || ticks === undefined) {
    return val;
  }

  let closest = val;
  const increasing = val > prev;
  let minDiff = Number.MAX_VALUE;

  // Binary search for the closest tick
  let left = 0;
  let right = ticks.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const tick = ticks[mid];
    const diff = Math.abs(tick - val);

    // Only update closest if the tick is in the right direction
    if (
      diff < minDiff &&
      ((increasing && tick >= val) || (!increasing && tick <= val))
    ) {
      closest = tick;
      if (closest === val) {
        break;
      }
      minDiff = diff;
    }

    if (tick < val) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return closest;
}

export function getCurrentStepAndSliderValue(
  stepMeta: StepMeta,
  stepFromConfig: number | undefined,
  snapToExistingStep: boolean,
  mediaSliderKey: string | undefined
): {currentStep: number; currentSliderValue: number} {
  const isDefaultSlider = mediaSliderKey == null || mediaSliderKey === '_step';
  const {
    min,
    max,
    steps,
    stepSet,
    minSliderStep,
    maxSliderStep,
    sliderSteps,
    sliderStepSet,
  } = stepMeta;

  const targetValue = stepFromConfig ?? (isDefaultSlider ? max : maxSliderStep);
  const shouldUseExactValue =
    stepFromConfig != null &&
    (!snapToExistingStep ||
      (isDefaultSlider ? stepSet : sliderStepSet).has(stepFromConfig));

  if (isDefaultSlider) {
    return {
      currentStep: shouldUseExactValue
        ? stepFromConfig
        : getClosestLowerStep(min, max, steps, targetValue),
      currentSliderValue: targetValue,
    };
  }

  // we still want to find the closest step for the slider value
  // but we don't want to snap the slider to it
  const closestSliderValue = getClosestLowerStep(
    minSliderStep,
    maxSliderStep,
    sliderSteps,
    targetValue
  );
  const sliderValue = shouldUseExactValue ? stepFromConfig : closestSliderValue;

  // For custom slider keys, map the slider value to the appropriate step
  // If the value is between slider steps, use the step corresponding to the lower slider value
  const sliderIndex = sliderSteps.indexOf(closestSliderValue);
  const stepIndex = Math.min(sliderIndex, steps.length - 1);

  return {
    currentStep: steps[stepIndex] ?? max,
    currentSliderValue: sliderValue,
  };
}

export function getStepFromSliderStep(
  runStepMeta: StepMetaRun,
  sliderStep?: number,
  snapToExistingStep: boolean = true
): {step: number; sliderStep?: number} {
  if (sliderStep == null) {
    const step = runStepMeta.steps[runStepMeta.steps.length - 1];
    return {
      step,
      sliderStep: step,
    };
  }

  // if there is not alternative slider key, the mapping will not be set
  if (runStepMeta.sliderKey === undefined) {
    const {min, max, steps} = runStepMeta;
    const step = snapToExistingStep
      ? getClosestLowerStep(min, max, steps, sliderStep)
      : sliderStep;
    return {
      step,
      sliderStep,
    };
  }

  const {
    minSliderStep: min,
    maxSliderStep: max,
    sliderSteps: steps,
  } = runStepMeta;

  const closestSliderStep = snapToExistingStep
    ? getClosestLowerStep(min, max, steps, sliderStep)
    : sliderStep;

  const step =
    runStepMeta.sliderStepMap?.get(closestSliderStep) ?? closestSliderStep;
  return {
    step,
    sliderStep: closestSliderStep,
  };
}

export function calculateStepBounds(steps: number[]) {
  return steps.reduce(
    (acc, step) => {
      acc.localResolveMin = Math.min(acc.localResolveMin, step);
      acc.localResolvedMax = Math.max(acc.localResolvedMax, step);
      return acc;
    },
    {localResolveMin: Infinity, localResolvedMax: -Infinity}
  );
}
